import { useCallback, useRef } from "react";
import { translateElement } from "../canvasUtils";

// Pointer-based dragging for one or many canvas elements.
// Live updates (no history) on each rAF frame; a single history checkpoint is
// taken at drag start so the whole drag collapses into one undo step.
export function useCanvasDrag({ svgRef, onLiveUpdateMany, onDragStart, onDragEnd }) {
  const rafRef = useRef(null);
  const dragRef = useRef(null);

  const getSvgPoint = useCallback(
    (clientX, clientY) => {
      // svgRef may be the root <svg> or a transformed <g>; map screen
      // coordinates into that node's user space via its own CTM.
      const node = svgRef.current;
      if (!node) return { x: 0, y: 0 };
      const root = node.ownerSVGElement || node;
      const ctm = node.getScreenCTM();
      if (!ctm) return { x: 0, y: 0 };
      const pt = root.createSVGPoint();
      pt.x = clientX;
      pt.y = clientY;
      const loc = pt.matrixTransform(ctm.inverse());
      return { x: loc.x, y: loc.y };
    },
    [svgRef]
  );

  // targets: array of element objects to move together.
  const startDrag = useCallback(
    (e, targets) => {
      if (!targets || !targets.length) return;
      // Cache the screen->user matrix ONCE at drag start. The pan/zoom view is
      // fixed during a drag, so the content group's CTM never changes — yet the
      // dragged element's geometry mutates every frame. Calling getScreenCTM()
      // on every pointermove forced a synchronous layout/reflow right after that
      // mutation, and for text the browser re-shapes the glyphs each time
      // (expensive for CJK + custom fonts) — a classic read/write layout thrash
      // that made text dragging feel laggy. Reusing the cached inverse matrix
      // turns each move into pure math, so dragging stays smooth.
      const node = svgRef.current;
      const root = node?.ownerSVGElement || node;
      const ctmInv = node?.getScreenCTM?.()?.inverse?.() || null;
      const toLocal = (clientX, clientY) => {
        if (!ctmInv || !root) return getSvgPoint(clientX, clientY);
        const pt = root.createSVGPoint();
        pt.x = clientX;
        pt.y = clientY;
        const loc = pt.matrixTransform(ctmInv);
        return { x: loc.x, y: loc.y };
      };

      const p = toLocal(e.clientX, e.clientY);
      dragRef.current = {
        startX: p.x,
        startY: p.y,
        originals: targets.map((t) => ({ id: t.id, el: t })),
        latest: p,
      };
      onDragStart?.();
      e.currentTarget.setPointerCapture?.(e.pointerId);

      const onMove = (ev) => {
        if (!dragRef.current) return;
        dragRef.current.latest = toLocal(ev.clientX, ev.clientY);
        if (rafRef.current) return;
        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = null;
          const d = dragRef.current;
          if (!d) return;
          const dx = d.latest.x - d.startX;
          const dy = d.latest.y - d.startY;
          onLiveUpdateMany(d.originals.map((o) => ({ id: o.id, attrs: translateElement(o.el, dx, dy) })));
        });
      };
      const onUp = () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
          rafRef.current = null;
        }
        dragRef.current = null;
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        onDragEnd?.();
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [svgRef, getSvgPoint, onLiveUpdateMany, onDragStart, onDragEnd]
  );

  return { startDrag, getSvgPoint };
}
